import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ClockCircleOutlined } from "@ant-design/icons";
import { Trans, useTranslation } from "react-i18next";
import { authService } from "../../services/authService";
import i18n from "../../i18n";
import SelecteurLangue from '../../components/SelecteurLangue';

/* ─── Icons ──────────────────────────────────────────────────── */
const EyeOpen = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const EyeClosed = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);
const LockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);
const MailIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
  </svg>
);
const ShieldIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);
const CheckIcon = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const MapPinIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
  </svg>
);
const GlobeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
  </svg>
);
const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

/* ─── Password helpers ───────────────────────────────────────── */
function getStrength(p) {
  if (!p) return { score: 0, label: "", color: "transparent" };
  let s = 0;
  if (p.length >= 8) s++;
  if (p.length >= 12) s++;
  if (/[A-Z]/.test(p)) s++;
  if (/[0-9]/.test(p)) s++;
  if (/[^a-zA-Z0-9]/.test(p)) s++;
  if (s <= 1) return { score: 1, label: i18n.t("auth:activation.force.faible"), color: "#e25959" };
  if (s <= 3) return { score: 2, label: i18n.t("auth:activation.force.modere"), color: "#f59e0b" };
  return { score: 3, label: i18n.t("auth:activation.force.fort"), color: "#1a6b3c" };
}

function getCriteria(p) {
  return [
    { label: i18n.t("auth:activation.criteres.longueur"), ok: p.length >= 8 },
    { label: i18n.t("auth:activation.criteres.majuscule"), ok: /[A-Z]/.test(p) },
    { label: i18n.t("auth:activation.criteres.chiffre"), ok: /[0-9]/.test(p) },
    { label: i18n.t("auth:activation.criteres.special"), ok: /[^a-zA-Z0-9]/.test(p) },
  ];
}

/* ─── Left panel (shared across states) ─────────────────────── */
function LeftPanel({ imgError, setImgError }) {
  const { t } = useTranslation("auth");
  return (
    <div className="rp-left">
      <div className="rp-left-inner">
        <div className="rp-logo">
          {!imgError ? (
            <img
              className="rp-logo-img"
              src="https://cartebrune.org/squelettes/imgs/logo-fr.png"
              alt={t("activation.panneau.logo")}
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="rp-logo-fallback">CB</div>
          )}
          <div>
            <div className="rp-logo-name">{t("activation.panneau.nom")}</div>
            <div className="rp-logo-sub">{t("activation.panneau.bureau")}</div>
          </div>
        </div>

        <div className="rp-accent-line" />

        <h2 className="rp-left-title">
          <Trans t={t} i18nKey="activation.panneau.titre" components={{ 1: <br />, 2: <em />, 3: <br /> }} />
        </h2>
        <p className="rp-left-desc">
          {t("activation.panneau.description")}
        </p>

        <div className="rp-facts">
          <div className="rp-fact">
            <div className="rp-fact-icon"><GlobeIcon /></div>
            <div className="rp-fact-text">
              <strong>{t("activation.panneau.etatsMembres")}</strong><br />
              {t("activation.panneau.etatsMembresTexte")}
            </div>
          </div>
          <div className="rp-fact">
            <div className="rp-fact-icon"><CalendarIcon /></div>
            <div className="rp-fact-text">
              <strong>{t("activation.panneau.institue")}</strong><br />
              {t("activation.panneau.institueTexte")}
            </div>
          </div>
          <div className="rp-fact">
            <div className="rp-fact-icon"><MapPinIcon /></div>
            <div className="rp-fact-text">
              <strong>{t("activation.panneau.secretariat")}</strong><br />
              128, Bvd du 13 Janvier, Immeuble BIDC
            </div>
          </div>
        </div>

        <div className="rp-left-footer">
          (+228) 22 22 39 55 · contact@cartebrune.org<br />
          cartebrune.org
        </div>
      </div>
    </div>
  );
}

/* ─── State: loading ─────────────────────────────────────────── */
function StateLoading() {
  const { t } = useTranslation("auth");
  return (
    <div className="rp-state-center">
      <div className="rp-spinner" />
      <p className="rp-state-msg">{t("activation.verificationLien")}</p>
    </div>
  );
}

/* ─── State: error ───────────────────────────────────────────── */
function StateError({ message, supportEmail }) {
  const { t } = useTranslation("auth");
  const isExpired = message && message.toLowerCase().includes("expir");

  return (
    <div className="rp-state-center">
      <div className="rp-state-icon rp-state-icon--err">
        {isExpired ? <ClockCircleOutlined /> : "✕"}
      </div>
      <h2 className="rp-state-title">
        {isExpired ? t("activation.erreur.lienExpire") : t("activation.erreur.lienInvalide")}
      </h2>
      <p className="rp-state-body">
        {message || t("activation.erreur.plusValide")}
      </p>
      {isExpired && (
        <p className="rp-state-hint">
          {t("activation.erreur.conseilExpire")}
        </p>
      )}
      {supportEmail && (
        <a href={`mailto:${supportEmail}`} className="rp-contact-link">
          {t("activation.erreur.contacterSupport", { email: supportEmail })}
        </a>
      )}
    </div>
  );
}

/* ─── State: already active ──────────────────────────────────── */
function StateAlreadyActive({ navigate }) {
  const { t } = useTranslation("auth");
  return (
    <div className="rp-state-center">
      <div className="rp-state-icon rp-state-icon--ok">✓</div>
      <h2 className="rp-state-title">{t("activation.dejaActif.titre")}</h2>
      <p className="rp-state-body">
        {t("activation.dejaActif.texte")}
      </p>
      <button className="rp-btn rp-btn--inline" onClick={() => navigate("/login")}>
        {t("activation.dejaActif.allerConnexion")}
      </button>
    </div>
  );
}

/* ─── State: form ────────────────────────────────────────────── */
function StateForm({ token, activationInfo, onSuccess }) {
  const { t } = useTranslation("auth");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [focused, setFocused] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);

  const strength = getStrength(password);
  const criteria = getCriteria(password);
  const passwordsMatch = password && confirm && password === confirm;
  const allCritOk = criteria.every(c => c.ok);
  const canSubmit = allCritOk && passwordsMatch && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setApiError(null);
    try {
      await authService.activateAccount({ token, password });
      onSuccess();
    } catch (err) {
      setApiError(
        err?.response?.data?.message ||
        t("activation.formulaire.erreur")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <h1 className="rp-form-title">
        <Trans t={t} i18nKey="activation.formulaire.titre" components={{ 1: <span /> }} />
      </h1>
      <p className="rp-form-sub">
        {t("activation.formulaire.sousTitre")}
      </p>

      <form onSubmit={handleSubmit}>

        {/* Email (préchargé, read-only) */}
        <div className="rp-field">
          <div className="rp-label"><MailIcon /> {t("activation.formulaire.adresseEmail")}</div>
          <div className="rp-input-wrap">
            <input
              className="rp-input locked"
              type="email"
              value={activationInfo?.email || ""}
              readOnly
              tabIndex={-1}
            />
            <div className="rp-email-badge"><CheckIcon /></div>
          </div>
        </div>

        <div className="rp-divider" />

        {/* Mot de passe */}
        <div className="rp-field">
          <div className="rp-label"><LockIcon /> {t("activation.formulaire.motDePasse")}</div>
          <div className="rp-input-wrap">
            <input
              className="rp-input"
              type={showPass ? "text" : "password"}
              value={password}
              onChange={e => setPassword(e.target.value)}
              onFocus={() => setFocused("pass")}
              onBlur={() => setFocused(null)}
              placeholder={t("activation.formulaire.exempleMotDePasse")}
              autoComplete="new-password"
            />
            <button type="button" className="rp-eye" onClick={() => setShowPass(v => !v)} tabIndex={-1}>
              {showPass ? <EyeClosed /> : <EyeOpen />}
            </button>
          </div>

          {/* Barre de force */}
          <div className={`rp-strength${password ? " visible" : ""}`}>
            <div className="rp-strength-bars">
              {[1, 2, 3].map(i => (
                <div key={i} className="rp-bar"
                  style={{ background: i <= strength.score ? strength.color : undefined }} />
              ))}
            </div>
            <span className="rp-strength-lbl" style={{ color: strength.color }}>
              {strength.label}
            </span>
          </div>

          {/* Critères */}
          <div className={`rp-criteria${(focused === "pass" || password) ? " visible" : ""}`}>
            {criteria.map((c, i) => (
              <div key={i} className={`rp-crit${c.ok ? " ok" : ""}`}>
                <div className="rp-crit-dot">{c.ok && <CheckIcon />}</div>
                {c.label}
              </div>
            ))}
          </div>
        </div>

        {/* Confirmation */}
        <div className="rp-field">
          <div className="rp-label"><LockIcon /> {t("activation.formulaire.confirmer")}</div>
          <div className="rp-input-wrap">
            <input
              className={`rp-input${confirm ? (passwordsMatch ? " match" : " nomatch") : ""}`}
              type={showConfirm ? "text" : "password"}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder={t("activation.formulaire.repetez")}
              autoComplete="new-password"
            />
            <button type="button" className="rp-eye" onClick={() => setShowConfirm(v => !v)} tabIndex={-1}>
              {showConfirm ? <EyeClosed /> : <EyeOpen />}
            </button>
          </div>
          <div className={`rp-match-hint${confirm ? " visible" : ""} ${passwordsMatch ? "ok" : "err"}`}>
            {confirm && (
              passwordsMatch
                ? <><span>✓</span> {t("activation.formulaire.correspondent")}</>
                : <><span>✕</span> {t("activation.formulaire.neCorrespondentPas")}</>
            )}
          </div>
        </div>

        {/* Erreur API */}
        {apiError && (
          <div className="rp-api-error">{apiError}</div>
        )}

        <button type="submit" className="rp-btn" disabled={!canSubmit}>
          {submitting ? t("activation.formulaire.activationEnCours") : t("activation.formulaire.activer")}
        </button>

        <div className="rp-security-note">
          <ShieldIcon />
          <span>
            <Trans t={t} i18nKey="activation.formulaire.noteSecurite" components={{ 1: <strong /> }} />
          </span>
        </div>

      </form>
    </>
  );
}

/* ─── State: success ─────────────────────────────────────────── */
function StateSuccess({ navigate }) {
  const { t } = useTranslation("auth");
  useEffect(() => {
    const timer = setTimeout(() => navigate("/login"), 4000);
    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div className="rp-success">
      <div className="rp-success-icon">✓</div>
      <h2 className="rp-success-title">{t("activation.succes.titre")}</h2>
      <p className="rp-success-sub">
        {t("activation.succes.enregistre")}<br />
        {t("activation.succes.redirection")}
      </p>
      <button className="rp-btn rp-btn--inline" style={{ marginTop: 24 }} onClick={() => navigate("/login")}>
        {t("activation.succes.seConnecter")}
      </button>
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────── */
export default function Registration() {
  const { token } = useParams();
  const navigate = useNavigate();

  // Statuts possibles : "loading" | "error" | "already_active" | "form" | "success"
  const [status, setStatus] = useState("loading");
  const [activationInfo, setActivationInfo] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setTimeout(() => setMounted(true), 60);
  }, []);

  useEffect(() => {
    if (!token) {
      setErrorMessage(i18n.t("auth:activation.erreur.invalideOuManquant"));
      setStatus("error");
      return;
    }

    authService.validateActivation(token)
      .then(data => {
        setActivationInfo(data);
        setStatus("form");
      })
      .catch(err => {
        const statusCode = err?.response?.status;
        const msg = err?.response?.data?.message || "";

        if (statusCode === 409) {
          setStatus("already_active");
        } else if (statusCode === 410 || msg.toLowerCase().includes("expir")) {
          // L'anglais doit garder « expir » : StateError s'en sert pour choisir son titre.
          setErrorMessage(msg || i18n.t("auth:activation.erreur.expire"));
          setStatus("error");
        } else {
          setErrorMessage(msg || i18n.t("auth:activation.erreur.invalide"));
          setStatus("error");
        }
      });
  }, [token]);

  /* ── Right panel content ── */
  const renderRight = () => {
    switch (status) {
      case "loading":
        return <StateLoading />;
      case "error":
        return (
          <StateError
            message={errorMessage}
            supportEmail={activationInfo?.supportEmail}
          />
        );
      case "already_active":
        return <StateAlreadyActive navigate={navigate} />;
      case "form":
        return (
          <StateForm
            token={token}
            activationInfo={activationInfo}
            onSuccess={() => setStatus("success")}
          />
        );
      case "success":
        return <StateSuccess navigate={navigate} />;
      default:
        return null;
    }
  };

  return (
    <>
      <SelecteurLangue variante="clair" flottant />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        body {
          font-family: 'DM Sans', sans-serif;
          background: #eef1ee;
          min-height: calc(100vh - var(--cb-banniere-h, 0px));
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
        }

        .rp-wrap {
          width: 100%;
          max-width: 960px;
          display: flex;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 24px 72px rgba(0,0,0,0.16);
          opacity: 0;
          transform: translateY(20px);
          transition: opacity .65s ease, transform .65s ease;
        }
        .rp-wrap.mounted { opacity: 1; transform: translateY(0); }

        /* ── Left panel ── */
        .rp-left {
          flex: 0 0 340px;
          background: #1a6b3c;
          display: flex;
          flex-direction: column;
          padding: 48px 38px;
          position: relative;
          overflow: hidden;
        }
        .rp-left::before {
          content: '';
          position: absolute;
          top: -90px; right: -90px;
          width: 280px; height: 280px;
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 50%;
        }
        .rp-left::after {
          content: '';
          position: absolute;
          bottom: -60px; left: -60px;
          width: 200px; height: 200px;
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 50%;
        }
        .rp-left-inner {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .rp-logo { display: flex; align-items: center; gap: 12px; margin-bottom: 36px; }
        .rp-logo-img {
          width: 52px; height: 52px;
          object-fit: contain;
          filter: brightness(0) invert(1);
          opacity: .9;
        }
        .rp-logo-fallback {
          width: 52px; height: 52px;
          border-radius: 10px;
          background: rgba(255,255,255,0.15);
          display: flex; align-items: center; justify-content: center;
          font-family: 'Playfair Display', serif;
          font-size: 18px; font-weight: 600; color: #fff;
        }
        .rp-logo-name { font-family: 'Playfair Display', serif; font-size: 15px; font-weight: 600; color: #fff; line-height: 1.25; }
        .rp-logo-sub { font-size: 10.5px; color: rgba(255,255,255,0.5); letter-spacing: 0.08em; text-transform: uppercase; margin-top: 3px; }
        .rp-accent-line { width: 36px; height: 2px; background: rgba(255,255,255,0.3); border-radius: 2px; margin-bottom: 26px; }
        .rp-left-title { font-family: 'Playfair Display', serif; font-size: 24px; font-weight: 400; color: #fff; line-height: 1.4; margin-bottom: 12px; }
        .rp-left-title em { font-style: italic; color: rgba(255,255,255,0.7); }
        .rp-left-desc { font-size: 12.5px; color: rgba(255,255,255,0.55); line-height: 1.75; font-weight: 300; margin-bottom: 30px; }
        .rp-facts { display: flex; flex-direction: column; gap: 14px; }
        .rp-fact { display: flex; align-items: flex-start; gap: 10px; }
        .rp-fact-icon { width: 30px; height: 30px; border-radius: 8px; background: rgba(255,255,255,0.1); display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: rgba(255,255,255,0.8); }
        .rp-fact-text { font-size: 11.5px; color: rgba(255,255,255,0.55); line-height: 1.6; }
        .rp-fact-text strong { color: rgba(255,255,255,0.9); font-weight: 500; }
        .rp-left-footer { margin-top: auto; padding-top: 22px; border-top: 1px solid rgba(255,255,255,0.12); font-size: 11px; color: rgba(255,255,255,0.38); line-height: 1.7; }

        /* ── Right panel ── */
        .rp-right {
          flex: 1;
          background: #ffffff;
          padding: 52px 48px 48px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        /* ── State: centered display ── */
        .rp-state-center { text-align: center; padding: 24px 16px; }
        .rp-state-icon {
          width: 72px; height: 72px;
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 30px;
          margin: 0 auto 24px;
        }
        .rp-state-icon--ok { background: #f2f8f4; border: 1.5px solid rgba(26,107,60,0.3); color: #1a6b3c; }
        .rp-state-icon--err { background: #fff7f7; border: 1.5px solid rgba(226,89,89,0.3); color: #e25959; }
        .rp-state-title { font-family: 'Playfair Display', serif; font-size: 26px; color: #111; margin-bottom: 10px; }
        .rp-state-msg { font-size: 13.5px; color: #aaa; margin-top: 14px; }
        .rp-state-body { font-size: 13.5px; color: #777; font-weight: 300; line-height: 1.7; margin-bottom: 10px; }
        .rp-state-hint { font-size: 12px; color: #aaa; line-height: 1.6; margin: 8px 0 16px; }
        .rp-contact-link { font-size: 12.5px; color: #1a6b3c; display: inline-block; margin-top: 6px; }

        /* ── Spinner ── */
        .rp-spinner {
          width: 42px; height: 42px;
          border: 3px solid #e6eae6;
          border-top-color: #1a6b3c;
          border-radius: 50%;
          animation: spin .8s linear infinite;
          margin: 0 auto 16px;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* ── Form ── */
        .rp-form-title { font-family: 'Playfair Display', serif; font-size: 28px; font-weight: 600; color: #111; margin-bottom: 8px; line-height: 1.2; }
        .rp-form-title span { color: #1a6b3c; }
        .rp-form-sub { font-size: 13.5px; color: #999; font-weight: 300; margin-bottom: 36px; line-height: 1.5; }

        .rp-field { margin-bottom: 18px; }
        .rp-label { display: flex; align-items: center; gap: 6px; font-size: 10.5px; font-weight: 500; letter-spacing: 0.1em; text-transform: uppercase; color: #aaa; margin-bottom: 8px; }
        .rp-input-wrap { position: relative; }
        .rp-input { width: 100%; background: #f7f9f7; border: 1.5px solid #e6eae6; border-radius: 10px; padding: 13px 44px 13px 14px; font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 400; color: #111; outline: none; transition: border-color .2s, background .2s, box-shadow .2s; }
        .rp-input::placeholder { color: #c0c8c0; }
        .rp-input:focus { border-color: #1a6b3c; background: #f2f8f4; box-shadow: 0 0 0 3px rgba(26,107,60,0.1); }
        .rp-input.locked { color: #aaa; background: #f5f5f5; border-color: #eee; cursor: not-allowed; }
        .rp-input.match { border-color: #1a6b3c; background: #f2f8f4; }
        .rp-input.nomatch { border-color: #e25959; background: #fff7f7; }

        .rp-eye { position: absolute; right: 13px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; color: #ccc; padding: 4px; display: flex; align-items: center; transition: color .2s; }
        .rp-eye:hover { color: #1a6b3c; }
        .rp-email-badge { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); width: 22px; height: 22px; border-radius: 50%; background: rgba(26,107,60,0.12); display: flex; align-items: center; justify-content: center; color: #1a6b3c; }

        .rp-strength { margin-top: 9px; opacity: 0; transition: opacity .3s; }
        .rp-strength.visible { opacity: 1; }
        .rp-strength-bars { display: flex; gap: 4px; margin-bottom: 5px; }
        .rp-bar { height: 3px; flex: 1; border-radius: 2px; background: #eee; transition: background .35s; }
        .rp-strength-lbl { font-size: 11px; font-weight: 500; letter-spacing: 0.06em; text-transform: uppercase; }

        .rp-criteria { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-top: 10px; max-height: 0; overflow: hidden; opacity: 0; transition: max-height .35s, opacity .3s; }
        .rp-criteria.visible { max-height: 80px; opacity: 1; }
        .rp-crit { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #ccc; transition: color .3s; }
        .rp-crit.ok { color: #1a6b3c; }
        .rp-crit-dot { width: 16px; height: 16px; border-radius: 50%; border: 1.5px solid #ddd; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all .25s; }
        .rp-crit.ok .rp-crit-dot { background: rgba(26,107,60,0.12); border-color: #1a6b3c; color: #1a6b3c; }

        .rp-match-hint { margin-top: 7px; font-size: 12px; display: flex; align-items: center; gap: 5px; opacity: 0; transition: opacity .3s; }
        .rp-match-hint.visible { opacity: 1; }
        .rp-match-hint.ok { color: #1a6b3c; }
        .rp-match-hint.err { color: #e25959; }

        .rp-divider { height: 1px; background: linear-gradient(90deg, transparent, #eee, transparent); margin: 18px 0; }

        .rp-api-error { background: #fff7f7; border: 1px solid rgba(226,89,89,0.3); border-radius: 9px; padding: 11px 14px; font-size: 12.5px; color: #c0392b; margin-bottom: 14px; line-height: 1.55; }

        .rp-btn { width: 100%; padding: 14px; background: #1a6b3c; border: none; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 13.5px; font-weight: 500; letter-spacing: 0.1em; text-transform: uppercase; color: #fff; cursor: pointer; transition: all .25s; }
        .rp-btn:hover:not(:disabled) { background: #155c32; box-shadow: 0 8px 24px rgba(26,107,60,0.28); transform: translateY(-1px); }
        .rp-btn:active:not(:disabled) { transform: translateY(0); }
        .rp-btn:disabled { background: #e8ebe8; color: #b0b8b0; cursor: not-allowed; }
        .rp-btn--inline { width: auto; padding: 12px 28px; }

        .rp-security-note { display: flex; align-items: flex-start; gap: 8px; margin-top: 16px; padding: 11px 13px; background: #f2f8f4; border: 1px solid rgba(26,107,60,0.15); border-radius: 9px; font-size: 11.5px; color: #2a7a4a; line-height: 1.55; }
        .rp-security-note svg { flex-shrink: 0; margin-top: 1px; }

        /* ── Success ── */
        .rp-success { text-align: center; animation: fadeInUp .5s ease forwards; }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .rp-success-icon { width: 64px; height: 64px; background: #f2f8f4; border: 1.5px solid rgba(26,107,60,0.3); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 24px; font-size: 26px; color: #1a6b3c; }
        .rp-success-title { font-family: 'Playfair Display', serif; font-size: 26px; color: #111; margin-bottom: 10px; }
        .rp-success-sub { font-size: 13.5px; color: #888; font-weight: 300; line-height: 1.7; }

        @media (max-width: 720px) {
          .rp-wrap { flex-direction: column; }
          .rp-left { flex: none; padding: 36px 28px; }
          .rp-facts { display: none; }
          .rp-right { padding: 36px 28px; }
          .rp-criteria { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className={`rp-wrap${mounted ? " mounted" : ""}`}>
        <LeftPanel imgError={imgError} setImgError={setImgError} />

        <div className="rp-right">
          {renderRight()}
        </div>
      </div>
    </>
  );
}